/**
 * Persuasion CSV Loader
 * P20250925-125 — Multiple business ideas per domain
 */

import { z } from 'zod';

const persuasionSchema = z.object({
  slug: z.string().min(1),
  idea_id: z.coerce.number().int().positive(),
  business_idea: z.string().min(1),
  website_use_case: z.string().min(1),
  value_prop: z.string().min(1),
  elevator_pitch: z.string().min(1),
  target_market: z.string().min(1),
  proof_points: z.string().optional(),
  tone: z.string().optional(),
  logo_text_override: z.string().optional(),
  priority: z.coerce.number().int().positive(),
  active: z.coerce.boolean(),
  length: z.coerce.number().int().positive(),
  word_count: z.coerce.number().int().positive(),
  score: z.coerce.number().min(0).max(100),
});

export type PersuasionRecord = z.infer<typeof persuasionSchema>;

let cachedPersuasion: PersuasionRecord[] | null = null;
let cacheTime: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Load and parse persuasion CSV
 */
export async function loadPersuasion(): Promise<PersuasionRecord[]> {
  const now = Date.now();

  // Return cached if fresh
  if (cachedPersuasion && now - cacheTime < CACHE_TTL) {
    return cachedPersuasion;
  }

  try {
    const response = await fetch('/data/persuasion.csv');
    if (!response.ok) throw new Error('Failed to fetch persuasion');

    const text = await response.text();
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map((h) => h.trim());

    const records: PersuasionRecord[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const row: Record<string, string> = {};

      headers.forEach((header, idx) => {
        row[header] = values[idx]?.trim() || '';
      });

      try {
        const parsed = persuasionSchema.parse(row);
        if (parsed.active) {
          records.push(parsed);
        }
      } catch (err) {
        console.warn(`[Persuasion] Invalid row ${i}:`, err);
      }
    }

    cachedPersuasion = records;
    cacheTime = now;

    return records;
  } catch (error) {
    console.error('[Persuasion] Load error:', error);
    return cachedPersuasion || [];
  }
}

/**
 * Get persuasion ideas for a slug (sorted by priority)
 */
export async function getPersuasionForSlug(slug: string): Promise<PersuasionRecord[]> {
  const persuasion = await loadPersuasion();

  return persuasion.filter((r) => r.slug === slug).sort((a, b) => a.priority - b.priority);
}

/**
 * Clear cache (for testing)
 */
export function clearPersuasionCache(): void {
  cachedPersuasion = null;
  cacheTime = 0;
}
